/**
 * Parse Small Qwen / vision bill summary text into styled sections.
 * Handles headings, "1. **Label** value", and label-on-own-line formats.
 */

function stripMd(value) {
  return String(value || '')
    .replace(/\*\*/g, '')
    .replace(/^#+\s*/, '')
    .replace(/^[-*]\s+/, '')
    .trim();
}

export function parseBillAttachmentSummary(raw) {
  const text = String(raw || '').replace(/\r\n/g, '\n').trim();
  if (!text) {
    return { title: '', sections: [], leftovers: '' };
  }

  const lines = text.split('\n').map((l) => l.trimEnd());
  let title = '';
  const sections = [];
  let current = null;
  const leftovers = [];

  const flush = () => {
    if (!current) return;
    current.value = current.valueParts.join('\n').trim();
    delete current.valueParts;
    if (current.label || current.value) sections.push(current);
    current = null;
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      if (current) current.valueParts.push('');
      continue;
    }

    if (/^#{1,3}\s+/.test(trimmed) && !sections.length && !current) {
      title = stripMd(trimmed);
      continue;
    }

    // "1. **Label** — value" or "1. **Label**: value" or "1. **Label** value"
    const numberedInline = trimmed.match(
      /^(\d{1,2})[.)]\s+\*\*(.+?)\*\*\s*(?:[—–\-:]+\s*)?(.*)$/
    );
    if (numberedInline) {
      flush();
      current = {
        index: numberedInline[1],
        label: stripMd(numberedInline[2]),
        valueParts: numberedInline[3] ? [stripMd(numberedInline[3])] : [],
      };
      continue;
    }

    // "1. Label" without bold
    const numberedPlain = trimmed.match(/^(\d{1,2})[.)]\s+(.+)$/);
    if (numberedPlain && !trimmed.includes('**')) {
      flush();
      const rest = stripMd(numberedPlain[2]);
      const split = rest.match(/^(.+?)\s*[—–\-:]\s+(.+)$/);
      current = {
        index: numberedPlain[1],
        label: split ? split[1].trim() : rest,
        valueParts: split ? [split[2].trim()] : [],
      };
      continue;
    }

    // "**Label**" alone as section header
    const boldOnly = trimmed.match(/^\*\*(.+?)\*\*\s*(?:[—–\-:]+\s*)?(.*)$/);
    if (boldOnly && /^\d{1,2}/.test(String(sections.length + 1))) {
      // treat as continuation unless it looks like a new field label
    }
    if (boldOnly && boldOnly[1].length < 80) {
      const maybeNewSection = /document|vendor|invoice|date|amount|line item|consultant|payment|match|risk/i.test(
        boldOnly[1]
      );
      if (maybeNewSection) {
        flush();
        current = {
          index: String(sections.length + 1),
          label: stripMd(boldOnly[1]),
          valueParts: boldOnly[2] ? [stripMd(boldOnly[2])] : [],
        };
        continue;
      }
    }

    if (current) {
      current.valueParts.push(stripMd(trimmed));
    } else if (!title) {
      title = stripMd(trimmed);
    } else {
      leftovers.push(stripMd(trimmed));
    }
  }

  flush();

  return {
    title: title || 'Attachment summary',
    sections,
    leftovers: leftovers.join('\n').trim(),
  };
}

export function summaryToneClass(label = '', value = '') {
  const blob = `${label} ${value}`.toLowerCase();
  if (/mismatch|risk|missing|not found|unclear|incomplete|disagree/.test(blob)) {
    return 'qwen-summary__item--warn';
  }
  if (/agree|match|found|invoice|total/.test(blob) && !/not found|mismatch/.test(blob)) {
    return 'qwen-summary__item--ok';
  }
  return '';
}

function parseMoney(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const matches = String(value).match(/\$?\s*-?\d{1,3}(?:,\d{3})*(?:\.\d{2})?|\$?\s*-?\d+(?:\.\d{2})?/g);
  if (!matches?.length) return null;
  // Prefer the last amount that looks like a total (often listed after subtotal/tax)
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const n = Number(String(matches[i]).replace(/[^0-9.-]/g, ''));
    if (Number.isFinite(n) && Math.abs(n) > 0) return n;
  }
  return null;
}

/**
 * Pull invoice/total amount from parsed summary sections or raw text.
 */
export function extractSummaryTotal(parsedOrRaw) {
  const parsed =
    parsedOrRaw && typeof parsedOrRaw === 'object' && Array.isArray(parsedOrRaw.sections)
      ? parsedOrRaw
      : parseBillAttachmentSummary(parsedOrRaw);

  const amountSection = (parsed.sections || []).find((s) =>
    /amount|total|grand\s*total|invoice\s*total/i.test(s.label || '')
  );
  if (amountSection?.value) {
    // Prefer explicit "Total: 1600" style lines inside the amounts block
    const lines = String(amountSection.value).split(/\n+/);
    for (const line of lines) {
      if (/total|grand/i.test(line) && !/subtotal/i.test(line)) {
        const n = parseMoney(line);
        if (n != null) return n;
      }
    }
    const fromSection = parseMoney(amountSection.value);
    if (fromSection != null) return fromSection;
  }

  const blob = [parsed.title, ...(parsed.sections || []).map((s) => `${s.label}\n${s.value}`), parsed.leftovers]
    .filter(Boolean)
    .join('\n');
  const totalLine = blob.split(/\n+/).find((line) => /total/i.test(line) && !/subtotal/i.test(line));
  if (totalLine) {
    const n = parseMoney(totalLine);
    if (n != null) return n;
  }
  return parseMoney(blob);
}

export function compareDraftAndExtractedTotal(draftAmount, extractedAmount, tolerance = 0.5) {
  const draft = parseMoney(draftAmount);
  const extracted = parseMoney(extractedAmount);
  if (draft == null || extracted == null) {
    return { draft, extracted, status: 'unknown' };
  }
  const diff = Math.abs(draft - extracted);
  return {
    draft,
    extracted,
    diff,
    status: diff <= tolerance ? 'match' : 'mismatch',
  };
}

function findSection(sections, pattern) {
  return (sections || []).find((s) => pattern.test(s.label || ''));
}

function firstLine(value) {
  return String(value || '')
    .split(/\n+/)
    .map((l) => l.trim())
    .find(Boolean) || '';
}

/** Drop draft-bill fields the model copies into captured mail (e.g. "Draft date: 09/04/2026"). */
function stripDraftSideLines(value) {
  return String(value || '')
    .split('\n')
    .filter((line) => {
      const t = line.trim().replace(/^[-*•]\s*/, '');
      if (!t) return true;
      return !/^(draft\s*(date|amount|bill\s*no\.?|bill\s*number|vendor(\s*email)?))\s*[:—–-]/i.test(
        t
      );
    })
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Flatten summary into bill-style detail rows (no scroll needed for review).
 * @param {object|string} parsedOrRaw
 * @param {{ formatCurrency?: Function, draft?: object }} options
 * draft: { vendorName, vendorEmail, originalAmount, consultants?, hours?, date? }
 * Bill number is never compared (draft nos never match vendor invoice nos).
 */
export function toBillDetailsCard(parsedOrRaw, options = {}) {
  const parsed =
    parsedOrRaw && typeof parsedOrRaw === 'object' && Array.isArray(parsedOrRaw.sections)
      ? parsedOrRaw
      : parseBillAttachmentSummary(parsedOrRaw);

  const sections = parsed.sections || [];
  const draft = options.draft || {};
  const total = extractSummaryTotal(parsed);
  const formatCurrency =
    typeof options.formatCurrency === 'function'
      ? options.formatCurrency
      : (n) => (n == null ? '—' : String(n));

  const doc = findSection(sections, /document\s*type/i);
  const vendor = findSection(sections, /vendor|payee/i);
  const invoiceNo = findSection(sections, /invoice|bill\s*number|bill\s*no/i);
  const dates = findSection(sections, /date/i);
  const amounts = findSection(sections, /amount|total/i);
  const lines = findSection(sections, /line\s*item/i);
  const labor = findSection(sections, /consultant|labor|hours/i);
  const match = findSection(sections, /match\s*vs|draft\s*bill/i);
  const risks = findSection(sections, /risk|missing/i);
  const mailBodySec = findSection(sections, /mail\s*body/i);
  const ocrSec = findSection(sections, /attachment\s*ocr|extracted\s*text/i);

  const used = new Set(
    [doc, vendor, invoiceNo, dates, amounts, lines, labor, match, risks, mailBodySec, ocrSec].filter(
      Boolean
    )
  );

  const draftVendor = String(draft.vendorName || '').trim().toLowerCase();
  const mailVendor = firstLine(vendor?.value).toLowerCase();
  const vendorMatched =
    draftVendor &&
    mailVendor &&
    mailVendor !== '—' &&
    (mailVendor.includes(draftVendor) ||
      draftVendor.includes(mailVendor) ||
      draftVendor.split(/\s+/).some((t) => t.length > 2 && mailVendor.includes(t)));

  const amountCompare = compareDraftAndExtractedTotal(draft.originalAmount, total);
  const totalMatched = amountCompare.status === 'match';

  const draftConsultants = Array.isArray(draft.consultants) ? draft.consultants : [];
  const laborText = `${labor?.value || ''}\n${lines?.value || ''}`.toLowerCase();
  const consultantMatched =
    draftConsultants.length > 0 &&
    draftConsultants.some((name) => {
      const n = String(name || '').trim().toLowerCase();
      if (!n) return false;
      const parts = n.split(/\s+/).filter((p) => p.length > 2);
      return parts.some((p) => laborText.includes(p)) || laborText.includes(n);
    });

  const hoursMatched =
    draft.hours != null &&
    Number(draft.hours) > 0 &&
    new RegExp(`\\b${Number(draft.hours)}\\b`).test(laborText);

  // Invoice / bill no is never shown — draft nos never match vendor invoice nos.
  // Section stays in `used` so it does not appear under other notes.

  const rows = [
    {
      key: 'type',
      label: 'Document type',
      value: firstLine(doc?.value) || '—',
      matchStatus: 'skip',
    },
    {
      key: 'vendor',
      label: 'Vendor',
      value: firstLine(vendor?.value) || '—',
      matchStatus: vendorMatched ? 'match' : mailVendor && mailVendor !== '—' ? 'mismatch' : 'skip',
    },
    {
      key: 'date',
      label: 'Date',
      value: stripDraftSideLines(dates?.value) || '—',
      matchStatus: 'skip',
    },
    {
      key: 'total',
      label: 'Total',
      value: total != null ? formatCurrency(total) : firstLine(amounts?.value) || '—',
      emphasize: true,
      matchStatus: totalMatched ? 'match' : amountCompare.status === 'mismatch' ? 'mismatch' : 'skip',
    },
    {
      key: 'lines',
      label: 'Line items',
      value: (lines?.value || '').trim() || '—',
      matchStatus: 'skip',
    },
    {
      key: 'labor',
      label: 'Consultants / hours',
      value: (labor?.value || lines?.value || '').trim() || '—',
      matchStatus: consultantMatched || hoursMatched ? 'match' : laborText.trim() ? 'skip' : 'skip',
    },
    {
      key: 'risks',
      label: 'Risks / missing',
      value: (risks?.value || '').trim() || '—',
      matchStatus: 'skip',
    },
  ].filter((r) => r.value && r.value !== '—');

  const more = sections
    .filter((s) => !used.has(s))
    .filter((s) => !/mail\s*body|attachment\s*ocr|extracted\s*text|match\s*vs/i.test(s.label || ''))
    .map((s) => ({
      key: `more-${s.index || s.label}`,
      label: s.label,
      value: stripDraftSideLines(s.value) || '—',
      matchStatus: 'skip',
    }));

  const extra = (more || []).filter((r) => r.value && r.value !== '—');
  if (parsed.leftovers) {
    const leftoverText = stripDraftSideLines(parsed.leftovers);
    if (leftoverText) {
      extra.push({
        key: 'notes',
        label: 'Other notes',
        value: leftoverText,
        matchStatus: 'skip',
      });
    }
  }

  const allRows = [...rows, ...extra];

  return {
    title: parsed.title || 'Bill details from attachment',
    rows: allRows.length
      ? allRows
      : sections.map((s, i) => ({
          key: `s-${i}`,
          label: s.label,
          value: s.value || '—',
          matchStatus: 'skip',
        })),
    more,
  };
}

function cleanPersonName(raw) {
  return String(raw || '')
    .replace(/\s+/g, ' ')
    .replace(/\b(service|period|hours?|rate|total|invoice)\b.*/i, '')
    .replace(/[.,;:]+$/g, '')
    .trim()
    .slice(0, 80);
}

function monthNameToNumber(name) {
  const key = String(name || '').trim().toLowerCase().slice(0, 3);
  const map = {
    jan: 1,
    feb: 2,
    mar: 3,
    apr: 4,
    may: 5,
    jun: 6,
    jul: 7,
    aug: 8,
    sep: 9,
    oct: 10,
    nov: 11,
    dec: 12,
  };
  return map[key] || null;
}

function toUsDateParts(monthName, day, year) {
  const month = monthNameToNumber(monthName);
  const d = Number(day);
  const y = Number(year);
  if (!month || !Number.isFinite(d) || !Number.isFinite(y)) return '';
  return `${String(month).padStart(2, '0')}/${String(d).padStart(2, '0')}/${y}`;
}

function parsePeriodParts(text) {
  const blob = String(text || '');
  const named = blob.match(
    /([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})\s*[-–—]\s*([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})/
  );
  if (named) {
    const from = toUsDateParts(named[1], named[2], named[3]);
    const to = toUsDateParts(named[4], named[5], named[6]);
    if (from && to) {
      return { fromDate: from, toDate: to, description: `${from} - ${to}` };
    }
  }
  const numeric = blob.match(
    /(\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2})\s*[-–—]\s*(\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2})/
  );
  if (numeric) {
    return {
      fromDate: numeric[1],
      toDate: numeric[2],
      description: `${numeric[1]} - ${numeric[2]}`,
    };
  }
  return { fromDate: '', toDate: '', description: '' };
}

function extractHoursFromText(text) {
  const blob = String(text || '');
  const hrs = blob.match(/(\d+(?:\.\d+)?)\s*hrs?\b/i);
  if (hrs) return Number(hrs[1]);
  const labeled = blob.match(/hours?\s*[:-]\s*(\d+(?:\.\d+)?)/i);
  if (labeled) return Number(labeled[1]);
  return null;
}

function extractRateFromText(text) {
  const blob = String(text || '');
  const rate = blob.match(
    /\$\s*(\d+(?:\.\d{1,2})?)\s*(?:\/\s*hr|per\s*hour|\/\s*hour|hourly)/i
  );
  if (rate) return Number(rate[1]);
  const labeled = blob.match(/(?:hourly\s*)?rate\s*[:-]\s*\$?\s*(\d+(?:\.\d{1,2})?)/i);
  if (labeled) return Number(labeled[1]);
  return null;
}

/**
 * Compact mail-side snapshot shaped like draft bill consultant rows.
 * Skips invoice/bill number comparison entirely.
 */
export function toMailBillSnapshot(parsedOrRaw, options = {}) {
  const parsed =
    parsedOrRaw && typeof parsedOrRaw === 'object' && Array.isArray(parsedOrRaw.sections)
      ? parsedOrRaw
      : parseBillAttachmentSummary(parsedOrRaw);

  const sections = parsed.sections || [];
  const draft = options.draft || {};
  const total = extractSummaryTotal(parsed);
  const formatCurrency =
    typeof options.formatCurrency === 'function'
      ? options.formatCurrency
      : (n) => (n == null ? '—' : String(n));

  const vendor = findSection(sections, /vendor|payee/i);
  const dates = findSection(sections, /date/i);
  const lines = findSection(sections, /line\s*item/i);
  const labor = findSection(sections, /consultant|labor|hours/i);
  const laborBlob = [labor?.value, lines?.value, parsed.leftovers].filter(Boolean).join('\n');

  const vendorName = firstLine(vendor?.value) || '';
  let invoiceDate = firstLine(dates?.value) || '';
  invoiceDate = invoiceDate
    .replace(/^invoice\s*date\s*[:-]?\s*/i, '')
    .replace(/^date\s*[:-]?\s*/i, '')
    .trim();

  const period = parsePeriodParts(laborBlob);
  const hours = extractHoursFromText(laborBlob);
  const rate = extractRateFromText(laborBlob);

  const names = [];
  const nameRe = /consultant\s*[:-]\s*([A-Za-z][A-Za-z .'-]{1,60})/gi;
  let nameMatch = nameRe.exec(laborBlob);
  while (nameMatch) {
    const cleaned = cleanPersonName(nameMatch[1]);
    if (cleaned && !names.some((n) => n.toLowerCase() === cleaned.toLowerCase())) {
      names.push(cleaned);
    }
    nameMatch = nameRe.exec(laborBlob);
  }

  // Fallback: draft consultant names present in the attachment text
  if (!names.length && Array.isArray(draft.consultants)) {
    draft.consultants.forEach((name) => {
      const n = String(name || '').trim();
      if (!n) return;
      const parts = n.split(/\s+/).filter((p) => p.length > 2);
      const hit =
        laborBlob.toLowerCase().includes(n.toLowerCase()) ||
        parts.every((p) => laborBlob.toLowerCase().includes(p.toLowerCase()));
      if (hit) names.push(n);
    });
  }

  const draftHours = Number(draft.hours);
  const draftRows = Array.isArray(draft.consultantRecords) ? draft.consultantRecords : [];

  const findDraftRow = (name) => {
    const lower = String(name || '').toLowerCase();
    if (!lower) return null;
    return (
      draftRows.find((r) => {
        const dn = String(r.consultantName || '').trim().toLowerCase();
        if (!dn) return false;
        return (
          lower.includes(dn) ||
          dn.includes(lower) ||
          dn.split(/\s+/).some((p) => p.length > 2 && lower.includes(p))
        );
      }) || null
    );
  };

  // If attachment text had no names but draft has consultants and vendor/total match later,
  // still surface draft consultants when labor text mentions them (handled above).
  // Extra fallback: when PDF named a period/hours but no person, use draft names if present.
  if (!names.length && draftRows.length && (period.fromDate || hours != null)) {
    draftRows.forEach((r) => {
      const n = String(r.consultantName || '').trim();
      if (n) names.push(n);
    });
  }

  const consultantRecords = names.map((consultantName, idx) => {
    const draftRow = findDraftRow(consultantName);
    const shareHoursFromText =
      hours != null && names.length > 1 ? Number((hours / names.length).toFixed(2)) : hours;

    let rowHours = shareHoursFromText;
    if (rowHours == null && draftRow?.hours != null && draftRow.hours !== '') {
      rowHours = Number(draftRow.hours);
    }
    if (rowHours == null && Number.isFinite(draftHours) && draftHours > 0 && names.length === 1) {
      rowHours = draftHours;
    }

    let rowRate = rate;
    if (rowRate == null) {
      const r = draftRow?.hourlyRate ?? draftRow?.rate;
      if (r != null && r !== '') rowRate = Number(r);
    }

    const fromDate =
      period.fromDate || draftRow?.fromDate || draftRow?.periodStart || '';
    const toDate = period.toDate || draftRow?.toDate || draftRow?.periodEnd || '';
    const description =
      period.description ||
      draftRow?.description ||
      ([fromDate, toDate].filter(Boolean).join(' - '));

    return {
      id: `mail-consultant-${idx}`,
      consultantName: consultantName || draftRow?.consultantName || 'Consultant',
      fromDate,
      toDate,
      description,
      hours: rowHours,
      hourlyRate: rowRate,
      total:
        rowHours != null && rowRate != null
          ? Number((rowHours * rowRate).toFixed(2))
          : draftRow?.total != null
            ? Number(draftRow.total)
            : null,
    };
  });

  const draftVendor = String(draft.vendorName || '').trim().toLowerCase();
  const mailVendor = vendorName.toLowerCase();
  const vendorMatched =
    draftVendor &&
    mailVendor &&
    (mailVendor.includes(draftVendor) ||
      draftVendor.includes(mailVendor) ||
      draftVendor.split(/\s+/).some((t) => t.length > 2 && mailVendor.includes(t)));

  const amountCompare = compareDraftAndExtractedTotal(draft.originalAmount, total);
  const totalMatched = amountCompare.status === 'match';

  const laborLower = laborBlob.toLowerCase();
  const consultantMatched =
    consultantRecords.length > 0 &&
    Array.isArray(draft.consultants) &&
    draft.consultants.some((name) => {
      const n = String(name || '').trim().toLowerCase();
      if (!n) return false;
      const parts = n.split(/\s+/).filter((p) => p.length > 2);
      return parts.some((p) => laborLower.includes(p)) || laborLower.includes(n);
    });

  const totalHours = consultantRecords.reduce(
    (sum, row) => sum + (parseFloat(row.hours) || 0),
    0
  );

  const hoursMatched =
    totalHours > 0 &&
    Number.isFinite(draftHours) &&
    draftHours > 0 &&
    Math.abs(totalHours - draftHours) < 0.05;

  return {
    vendorName: vendorName || '',
    dateLabel: invoiceDate || '',
    total,
    totalLabel: total != null ? formatCurrency(total) : '',
    consultantRecords,
    consultantCount: consultantRecords.length,
    totalHours,
    matches: {
      vendor: vendorMatched,
      total: totalMatched,
      consultant: consultantMatched,
      hours: hoursMatched,
    },
  };
}

export function polishReadableText(raw) {
  return String(raw || '')
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function pickReadableSource(polished, raw) {
  const p = polishReadableText(polished);
  const r = polishReadableText(raw);
  if (!p) return r;
  if (!r) return p;
  if (r.length > 120 && p.length < r.length * 0.45) return r;
  return p;
}

const SOURCE_KV_RE = /^([A-Za-z][A-Za-z0-9 /&().%-]{0,42})\s*[:—–]\s+(.+)$/;

export function toReadableSourceBlocks(raw) {
  const text = polishReadableText(raw);
  if (!text) return [];
  const blocks = [];
  let fieldRows = [];
  const flushFields = () => {
    if (!fieldRows.length) return;
    blocks.push({ type: 'fields', rows: fieldRows });
    fieldRows = [];
  };

  text.split('\n').forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushFields();
      return;
    }
    const bullet = trimmed.replace(/^[-*•]\s+/, '');
    const kv = bullet.match(SOURCE_KV_RE);
    if (kv) {
      fieldRows.push({ label: kv[1].trim(), value: kv[2].trim() });
      return;
    }
    flushFields();
    if (/^#{1,3}\s+/.test(trimmed)) {
      blocks.push({ type: 'heading', text: trimmed.replace(/^#+\s*/, '') });
      return;
    }
    blocks.push({ type: 'p', text: bullet });
  });
  flushFields();
  return blocks;
}

function parseFlexibleDate(text) {
  const t = String(text || '').trim();
  if (!t || /^not\s*found$/i.test(t) || t === '—') return null;
  const us = t.match(/\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/);
  if (us) return new Date(Number(us[3]), Number(us[1]) - 1, Number(us[2]));
  const iso = t.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const named = t.match(/\b([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})\b/);
  if (named) {
    const month = monthNameToNumber(named[1]);
    if (month) return new Date(Number(named[3]), month - 1, Number(named[2]));
  }
  return null;
}

function pickLabeledDate(blob, labels) {
  const text = stripDraftSideLines(blob);
  const lines = String(text || '').split(/\n+/);
  for (const label of labels) {
    const re = new RegExp(`^\\s*${label}\\s*[:-]?\\s*(.+)$`, 'i');
    for (const line of lines) {
      const m = String(line || '').trim().match(re);
      if (!m) continue;
      const parsed = parseFlexibleDate(m[1]);
      if (parsed) return parsed;
    }
  }
  return null;
}

function cleanExtractedValue(value) {
  const text = firstLine(value)
    .replace(/^\*{0,2}[^:]{0,40}:{0,1}\*{0,2}\s*/u, (m) => (m.includes(':') ? '' : m))
    .replace(/^[#*•-]+\s*/, '')
    .trim();
  if (!text || /^not\s*found$/i.test(text) || text === '—') return '';
  return text;
}

/**
 * Pull invoice number, invoice date, due date, and payment-terms text from a captured-mail review.
 */
export function extractCapturedBillFields(parsedOrRaw) {
  const parsed =
    parsedOrRaw && typeof parsedOrRaw === 'object' && Array.isArray(parsedOrRaw.sections)
      ? parsedOrRaw
      : parseBillAttachmentSummary(parsedOrRaw);

  const sections = parsed.sections || [];
  const invoiceNoSec = findSection(sections, /invoice|bill\s*number|bill\s*no/i);
  const datesSec = findSection(sections, /date/i);
  const paymentSec = findSection(sections, /payment|remittance|terms/i);
  const datesBlob = stripDraftSideLines(datesSec?.value);
  const paymentBlob = String(paymentSec?.value || '');

  let invoiceNumber = cleanExtractedValue(invoiceNoSec?.value);
  invoiceNumber = invoiceNumber
    .replace(/^(invoice|bill)\s*(number|no\.?|#)?\s*[:-]?\s*/i, '')
    .replace(/^#\s*/, '')
    .trim();
  if (/^not\s*found$/i.test(invoiceNumber)) invoiceNumber = '';

  const invoiceDate =
    pickLabeledDate(datesBlob, [
      'invoice\\s*date',
      'bill\\s*date',
      'date',
    ]) || parseFlexibleDate(firstLine(datesBlob));

  const dueDate =
    pickLabeledDate(datesBlob, ['due\\s*date', 'payment\\s*due', 'pay\\s*by']) ||
    pickLabeledDate(paymentBlob, ['due\\s*date', 'payment\\s*due', 'pay\\s*by']);

  let paymentTermsText = '';
  const termLine = String(paymentBlob)
    .split(/\n+/)
    .map((l) => l.trim())
    .find((l) => /net\s*\d+|due\s*on\s*receipt|upon\s*receipt|payment\s*terms?/i.test(l));
  if (termLine) {
    paymentTermsText = termLine
      .replace(/^(payment\s*)?(terms?|remittance)\s*[:-]?\s*/i, '')
      .trim();
  } else {
    paymentTermsText = cleanExtractedValue(paymentBlob);
  }
  if (/^not\s*found$/i.test(paymentTermsText)) paymentTermsText = '';

  return {
    invoiceNumber,
    invoiceDate,
    dueDate,
    paymentTermsText,
  };
}

export function capturedSourceFromSummary(parsedOrRaw, fallbacks = {}) {
  const parsed =
    parsedOrRaw && typeof parsedOrRaw === 'object' && Array.isArray(parsedOrRaw.sections)
      ? parsedOrRaw
      : parseBillAttachmentSummary(parsedOrRaw);
  const mailSec = findSection(parsed.sections, /mail\s*body/i);
  const ocrSec = findSection(parsed.sections, /attachment\s*ocr|extracted\s*text/i);
  const extracts = Array.isArray(fallbacks.extracts) ? fallbacks.extracts : [];
  const extractText = extracts
    .map((item) => String(item?.text || '').trim())
    .filter(Boolean)
    .join('\n\n');

  return {
    mailBody: pickReadableSource(mailSec?.value, fallbacks.mailBody),
    attachmentOcr: pickReadableSource(ocrSec?.value, extractText),
    extracts: extracts
      .map((item) => ({
        name: item?.name || 'Attachment',
        text: polishReadableText(item?.text),
        error: item?.error || '',
      }))
      .filter((item) => item.text || item.error),
  };
}
